<template>
  <section class="flex flex-col gap-3 rounded-3xl border border-edge bg-panel p-4 sm:gap-4 sm:p-6">
    <div class="flex items-start justify-between gap-2">
      <div class="flex flex-col gap-0.5">
        <h2 class="font-display text-[22px] font-extrabold sm:text-[26px]">Kasse</h2>
        <span class="text-[13px] text-slate-400 sm:text-sm">Rest-Bargeld pro Monat</span>
      </div>
      <span class="font-display text-[26px] font-extrabold text-beer sm:text-4xl">{{ formatEuro(total) }}</span>
    </div>

    <p v-if="months.length === 0" class="py-6 text-center text-slate-400">Noch keine Einträge in der Kasse.</p>

    <div
      v-else
      role="img"
      :aria-label="ariaLabel"
      class="grid h-32 items-end gap-1.5 sm:h-44 sm:gap-3"
      :style="{ gridTemplateColumns: `repeat(${months.length}, minmax(0, 1fr))` }"
    >
      <div v-for="(month, index) in months" :key="month.label" class="flex h-full flex-col items-center justify-end gap-1.5">
        <span class="hidden text-xs text-slate-300 sm:block">{{ formatEuro(month.value) }}</span>
        <div
          class="w-full rounded-b-sm rounded-t-md motion-safe:transition-[height] motion-safe:duration-700 motion-safe:ease-out"
          :class="index === months.length - 1 ? 'bg-beer' : 'bg-slate-700'"
          :style="{ height: grown ? month.height : '0%' }"
        ></div>
        <span class="text-[11px] text-slate-400 sm:text-xs">{{ month.short }}</span>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Subsidy from '@/types/CashData';
import { formatEuro, percentOf } from '@/utils/format';
import { useGrow } from '@/composables/useGrow';

const props = defineProps<{ subsidies: Subsidy[] }>();

const total = computed(() => props.subsidies.reduce((sum, s) => sum + s.value, 0));

const months = computed(() => {
  const max = Math.max(0, ...props.subsidies.map((s) => s.value));
  return props.subsidies.map((s) => ({
    label: s.month,
    short: s.month.slice(0, 3),
    value: s.value,
    height: percentOf(s.value, max),
  }));
});

const ariaLabel = computed(() => 'Kasse pro Monat: ' + months.value.map((m) => `${m.label} ${formatEuro(m.value)}`).join(', '));

const grown = useGrow(() => props.subsidies);
</script>
